import React, { useMemo } from "react";
import { Button, Chip, ChipDelete, Stack } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import type { FilterInterface } from "./SearchFilters";
import {
  leafKind,
  listNode,
  sortChildren,
  type FilterValues,
} from "./filterState";
import { formatAmount, renderPath, toLabel } from "./filterLabels";

export type FilterChip = {
  key: string;
  label: string;
  value: string;
  /** The filter values with this chip removed. */
  next: FilterValues;
};

const withoutLeaf = (values: FilterValues, path: string): FilterValues => {
  const { [path]: _removed, ...rest } = values;
  return rest;
};

const isSet = (bound: FilterInterface["min"]) =>
  bound !== undefined && bound !== null && bound !== "";

const formatBound = (
  bound: FilterInterface["min"],
  path: string,
  kind: "range" | "date",
) => {
  if (kind === "date") return String(bound).slice(0, 10);
  const n = Number(bound);
  if (Number.isNaN(n)) return String(bound);
  return formatAmount(n, path);
};

const describeBounds = (
  node: FilterInterface,
  path: string,
  kind: "range" | "date",
) => {
  const min = isSet(node.min) ? formatBound(node.min, path, kind) : null;
  const max = isSet(node.max) ? formatBound(node.max, path, kind) : null;
  if (min && max) return `${min} – ${max}`;
  if (min) return kind === "date" ? `from ${min}` : `≥ ${min}`;
  if (max) return kind === "date" ? `until ${max}` : `≤ ${max}`;
  return "any";
};

// One chip per selected list value, one per range or date filter, in the order
// the filter tree shows them. Labels carry the nearest group for context.
export const buildFilterChips = (
  schema: FilterInterface,
  values: FilterValues,
): FilterChip[] => {
  const chips: FilterChip[] = [];

  const walk = (node: FilterInterface, groupLabel: string | null) => {
    if (node.children) {
      const isRoot = node.path.trim() === "value";
      const label = isRoot ? groupLabel : renderPath(node.path);
      for (const child of sortChildren(node.children)) walk(child, label);
      return;
    }

    const kind = leafKind(node);
    const active = values[node.path];
    if (!kind || !active) return;

    const leafLabel = renderPath(node.path);
    const label =
      groupLabel && groupLabel !== leafLabel
        ? `${groupLabel} › ${leafLabel}`
        : leafLabel;

    if (kind === "list") {
      const selected = active.allowed_values ?? [];
      const contains = active.contains ?? [];
      const nextWith = (remaining: FilterInterface | null) =>
        remaining
          ? { ...values, [node.path]: remaining }
          : withoutLeaf(values, node.path);
      selected.forEach((value, index) => {
        chips.push({
          key: `${node.path}#${index}`,
          label,
          value: toLabel(value, node.type === "boolean"),
          next: nextWith(
            listNode(
              node,
              selected.filter((_, i) => i !== index),
              contains,
            ),
          ),
        });
      });
      contains.forEach((term, index) => {
        chips.push({
          key: `${node.path}~${index}`,
          label,
          value: `contains “${term}”`,
          next: nextWith(
            listNode(
              node,
              selected,
              contains.filter((_, i) => i !== index),
            ),
          ),
        });
      });
      return;
    }

    chips.push({
      key: node.path,
      label,
      value: describeBounds(active, node.path, kind),
      next: withoutLeaf(values, node.path),
    });
  };

  walk(schema, null);
  return chips;
};

type ActiveFiltersProps = {
  schema: FilterInterface | undefined;
  values: FilterValues;
  onChange: (next: FilterValues) => void;
  sx?: SxProps;
};

export const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  schema,
  values,
  onChange,
  sx,
}) => {
  const chips = useMemo(
    () => (schema ? buildFilterChips(schema, values) : []),
    [schema, values],
  );

  if (chips.length === 0) return null;

  return (
    <Stack
      role="group"
      aria-label="Active filters"
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
      sx={sx}
    >
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          variant="soft"
          color="primary"
          endDecorator={
            <ChipDelete
              aria-label={`Remove ${chip.label}: ${chip.value}`}
              onDelete={() => onChange(chip.next)}
            />
          }
        >
          <span style={{ opacity: 0.75 }}>{chip.label}:</span> {chip.value}
        </Chip>
      ))}
      <Button variant="plain" size="sm" onClick={() => onChange({})}>
        Clear all
      </Button>
    </Stack>
  );
};
